import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo-gandiaye-sm.webp";

const navItems = [
  { name: 'Accueil', path: '/' },
  { name: 'Ma Mairie', path: '/mairie' },
  { name: 'Découvrir Gandiaye', path: '/decouvrir' },
  { name: 'Vie Quotidienne', path: '/vie-quotidienne' },
  { name: 'Projets', path: '/projets' },
  { name: 'Actualités', path: '/medias' },
  { name: 'Agenda', path: '/medias?tab=agenda' },
  { name: 'Diaspora', path: '/diaspora' },
  { name: 'Documents', path: '/documents' },
  { name: 'Médiathèque', path: '/medias?tab=galerie' },
];

export const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const suggestionsTimeout = useRef(null);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setSearchOpen(false);
    setShowSuggestions(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = (q) => {
    if (suggestionsTimeout.current) clearTimeout(suggestionsTimeout.current);
    if (q.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    suggestionsTimeout.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/search/suggestions?q=${encodeURIComponent(q)}`);
        setSuggestions(res.data.suggestions || []);
        setShowSuggestions(true);
      } catch { setSuggestions([]); }
    }, 250);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/recherche?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (text) => {
    navigate(`/recherche?q=${encodeURIComponent(text)}`);
    setSearchOpen(false);
    setShowSuggestions(false);
    setSearchQuery('');
  };

  const isActive = (path) => location.pathname === path || (path !== '/' && location.pathname.startsWith(path));

  return (
    <>
      <div className="senegal-stripe fixed top-0 left-0 right-0 z-50" />

      <header className="fixed top-[6px] left-0 right-0 z-40 border-b border-white/10 bg-[#0F6B3E]/95 backdrop-blur-md shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1.5 lg:px-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src={LOGO_URL}
              alt="Logo Commune de Gandiaye"
              className="h-8 w-8 rounded-full border border-white/20 bg-white object-contain shadow-sm"
            />
            <div className="leading-tight">
              <h1 className="text-base font-extrabold text-white lg:text-lg" style={{ fontFamily: 'var(--font-heading)' }}>
                Commune de Gandiaye
              </h1>
              <p className="text-[10px] text-white/80 lg:text-xs">République du Sénégal</p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden items-center gap-1 lg:flex">
            {navItems.map((item) => (
              item.children ? (
                <DropdownMenu key={item.name}>
                  <DropdownMenuTrigger asChild>
                    <button className={`flex items-center gap-1 rounded-xl px-3 py-2 text-sm font-medium transition-all duration-300 ${isActive(item.path) ? 'bg-white/[0.12] text-white shadow-inner' : 'text-white/90 hover:bg-white/[0.08] hover:text-[#FCD116]'}`}>
                      {item.name} <ChevronDown size={14} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48 bg-white border-0 shadow-xl rounded-lg p-1">
                    {item.children.map((child) => (
                      <DropdownMenuItem key={child.path} asChild>
                        <Link to={child.path} className="cursor-pointer px-3 py-2 rounded hover:bg-gray-100">{child.name}</Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link key={item.name} to={item.path}
                  className={`rounded-xl px-3 py-2 text-sm font-medium transition-all duration-300 ${isActive(item.path) ? 'bg-white/[0.12] text-white shadow-inner' : 'text-white/90 hover:bg-white/[0.08] hover:text-[#FCD116]'}`}>
                  {item.name}
                </Link>
              )
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setSearchOpen(!searchOpen); setShowSuggestions(false); }}
              className="rounded-full p-2 text-white transition-all duration-300 hover:bg-white/10 hover:scale-105"
              data-testid="navbar-search-toggle"
            >
              {searchOpen ? <X size={20} /> : <Search size={20} />}
            </button>
            {/* Admin accessible par URL directe uniquement */}
            <button className="lg:hidden rounded-xl p-2 text-white transition hover:bg-white/10" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Search Bar Dropdown */}
        {searchOpen && (
          <div className="border-t border-white/20" style={{ background: 'rgba(0,0,0,0.15)' }}>
            <div className="mx-auto max-w-7xl px-4 py-3 lg:px-8" ref={searchRef}>
              <form onSubmit={handleSearchSubmit} className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" size={20} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); fetchSuggestions(e.target.value); }}
                  placeholder="Rechercher sur gandiaye.sn..."
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white text-neutral-800 text-base focus:outline-none focus:ring-2 focus:ring-emerald-400 shadow-lg"
                  autoFocus
                  data-testid="navbar-search-input"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border overflow-hidden z-50" data-testid="search-suggestions">
                    {suggestions.map((s, i) => (
                      <button key={i} type="button"
                        onClick={() => handleSuggestionClick(s.text)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-neutral-50 transition-colors border-b last:border-0">
                        <Search size={14} className="text-neutral-400 flex-shrink-0" />
                        <span className="text-sm text-neutral-800 font-medium truncate flex-1">{s.text}</span>
                        <span className="text-xs text-neutral-400 flex-shrink-0">{s.type}</span>
                      </button>
                    ))}
                  </div>
                )}
              </form>
            </div>
          </div>
        )}
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-30 bg-white pt-14 lg:hidden overflow-y-auto">
          <nav className="px-6 py-6">
            <form onSubmit={handleSearchSubmit} className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full pl-10 pr-4 py-3 bg-neutral-100 rounded-lg text-neutral-800 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  data-testid="mobile-search-input"
                />
              </div>
            </form>
            {navItems.map((item) => (
              <div key={item.name} className="border-b border-gray-100">
                {item.children ? (
                  <div className="py-3">
                    <span className="font-semibold text-gray-800">{item.name}</span>
                    <div className="mt-2 ml-4 space-y-2">
                      {item.children.map((child) => (
                        <Link key={child.path} to={child.path} className="block py-2 text-gray-600 hover:text-green-600">{child.name}</Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link to={item.path} className="block py-3 font-semibold text-gray-800 hover:text-green-600">{item.name}</Link>
                )}
              </div>
            ))}
            {/* Admin accessible par URL directe uniquement */}
          </nav>
        </div>
      )}

      <div className="navbar-spacer" />
    </>
  );
};

export default Navbar;
